/**
 * Engine Parameters Form Component
 *
 * Renders provider-specific parameter controls for image generation models.
 * Supports textarea, slider, number, and checkbox control types.
 * All fields are always editable so presets can be shared across models.
 */

import { Info } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  ProviderParams,
  ProviderParamDefinition,
  PROVIDER_PARAM_DEFINITIONS,
  getProviderParamsForModel,
} from '../types/image-generator.types';

export interface EngineParamsFormProps {
  modelName: string;
  providerParams: ProviderParams;
  onUpdate: (params: ProviderParams) => void;
  /** When true, shows all params (not just applicable ones) */
  showAllParams?: boolean;
}

export function EngineParamsForm({
  modelName,
  providerParams,
  onUpdate,
  showAllParams = false,
}: EngineParamsFormProps) {
  const applicableDefs = getProviderParamsForModel(modelName);
  const defs = showAllParams ? PROVIDER_PARAM_DEFINITIONS : applicableDefs;
  const applicableKeys = new Set(applicableDefs.map((d) => d.key));

  if (defs.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No engine-specific parameters for this model.
      </p>
    );
  }

  const handleChange = (key: string, value: unknown) => {
    onUpdate({ ...providerParams, [key]: value });
  };

  return (
    <div className="space-y-4">
      {defs.map((def) => {
        const isApplicable = applicableKeys.has(def.key);
        return (
          <EngineParamField
            key={def.key}
            definition={def}
            value={providerParams[def.key]}
            onChange={(value) => handleChange(def.key, value)}
            showApplicabilityNote={showAllParams && !isApplicable}
          />
        );
      })}
    </div>
  );
}

interface EngineParamFieldProps {
  definition: ProviderParamDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  showApplicabilityNote?: boolean;
}

function EngineParamField({
  definition,
  value,
  onChange,
  showApplicabilityNote = false,
}: EngineParamFieldProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label className="text-xs font-medium">{definition.label}</label>
        <div className="relative group/info">
          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg text-xs text-popover-foreground w-64 opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all z-[100]">
            {definition.description}
          </div>
        </div>
        {showApplicabilityNote && (
          <span className="text-[10px] text-muted-foreground italic ml-auto">
            Not used by current model
          </span>
        )}
      </div>

      {definition.controlType === 'textarea' && (
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter text..."
          className="text-xs min-h-[60px] resize-none"
          rows={2}
        />
      )}

      {definition.controlType === 'slider' && (
        <div className="flex items-center gap-3">
          <Slider
            value={[Number(value ?? definition.defaultValue)]}
            onValueChange={([v]) => onChange(v)}
            min={definition.min}
            max={definition.max}
            step={definition.step}
            className="flex-1"
          />
          <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">
            {Number(value ?? definition.defaultValue)}
          </span>
        </div>
      )}

      {definition.controlType === 'number' && (
        <Input
          type="number"
          value={value !== undefined && value !== '' ? String(value) : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') {
              onChange('');
            } else {
              const num = Number(v);
              if (
                !isNaN(num) &&
                num >= (definition.min ?? 0) &&
                num <= (definition.max ?? Infinity)
              ) {
                onChange(num);
              }
            }
          }}
          placeholder={`0 – ${definition.max?.toLocaleString()}`}
          className="text-xs h-8"
          min={definition.min}
          max={definition.max}
        />
      )}

      {definition.controlType === 'checkbox' && (
        <button
          type="button"
          role="checkbox"
          aria-checked={!!value}
          onClick={() => onChange(!value)}
          className={`h-5 w-5 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${
            value
              ? 'bg-primary border-primary'
              : 'border-muted-foreground/50 hover:border-muted-foreground'
          }`}
        >
          {!!value && (
            <svg
              className="h-3.5 w-3.5 text-primary-foreground"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}
